import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Download, ArrowUpDown, ArrowUp, ArrowDown, X, Loader2, Trash2, Plus, Check, Eye, ClipboardList, ShieldOff, Link2Off, RotateCcw } from 'lucide-react';
import { components } from 'react-select';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { queryKeys } from '../queryKeys.ts';
import {
  appendRiskProcessMappings,
  compositeRiskKey,
  riskLevelOf,
  strRiskField,
} from '../request/risks/functional.js';
import { fetchSoxCycles } from '../request/sox/soxDashboard.api.js';
import { useRiskProcessFilterOptions, useRisksDashboardMetrics, useRisksTableList } from '../hooks/risks/useRisksTableList.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import { TENANT_PATHS } from '../routing/paths.ts';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  soxProcessFlowchartPath,
} from '../routing/processNavigation.ts';
import {
  RISK_CATEGORIES,
  RISK_FILTER_OTHERS,
  getKnownRiskCategory,
  getRiskCategoryLabel,
  RISK_CATEGORY_BADGE_CLASSES
} from '../constants/riskCategories';
import AssociationCountCell from './risks-table/components/AssociationCountCell.tsx';
import AssociationPopover from './risks-table/components/AssociationPopover.js';
import BulkAssociationModal from './risks-table/components/BulkAssociationModal.js';
import {
  riskAssociationEntries,
  riskAssociationCount,
  riskControlAssociationGroups,
  riskProcessAssociations,
  RISK_ASSOCIATION_KIND,
} from './risks-table/utils.js';
import {
  RISK_ASSOCIATION_COLUMN_ORDER,
  RISK_ASSOCIATION_UI,
  RISK_FIELD_CYCLE_NAME,
  RISK_FIELD_NODE_DESCRIPTION,
  RISK_FIELD_RECOMMENDATION,
  RISK_FIELD_RISK_DESCRIPTION,
  RISK_SORT_KEY,
} from './risks-table/constants.js';
import {
  buildRiskCategoryFilterOptions,
  CONTROL_MAPPED_FILTER_OPTIONS,
  CONTROL_MAPPED_FILTER_UNSELECTED,
  CONTROL_MAPPED_FILTER_NO,
  PROCESS_MAPPED_FILTER_UNSELECTED,
  PROCESS_MAPPED_FILTER_NO,
  RISK_LEVEL_FILTER_OPTIONS,
  SOURCE_FILTER_OPTIONS,
} from './risks-table/filters.js';
import { readStoredRisksPageSize, writeStoredRisksPageSize } from './risks-table/pagination.js';
import { AppDataTable } from '../shared/table/AppDataTable.tsx';
import { AppSelect } from '../shared/common/AppSelect.tsx';
import { ListPageSearchInput } from '../shared/list-page/ListPageSearchInput.tsx';
import { Tooltip, TooltipProvider } from '../admin/components/shared/Tooltip.tsx';
import { Select } from './common/Select.js';
import { RiskProcessAssociations } from '../features/risks/components/RiskProcessAssociations.tsx';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

const RISKS_DEFAULT_COLUMN_VISIBILITY = {
  originVersion: false,
  domain: false,
};

/** True when the column is shown (TanStack: missing key = visible). */
function isRiskExportColumnVisible(columnVisibility, columnId) {
  return columnVisibility?.[columnId] !== false;
}

function formatRiskExportBracketLabel(id, name) {
  const idText = String(id ?? '').trim();
  const nameText = String(name ?? '').trim();
  if (idText && nameText) return `(${idText}) ${nameText}`;
  if (idText) return `(${idText})`;
  return nameText;
}

/** Linked processes as "(processId) name", comma-separated. */
function formatRiskExportProcessCell(risk) {
  return riskProcessAssociations(risk)
    .map((p) => formatRiskExportBracketLabel(p.processId, p.processName))
    .filter(Boolean)
    .join(', ');
}

/** Unique SOX cycles from linked processes, comma-separated. */
function formatRiskExportSoxCycleCell(risk) {
  const seen = new Set();
  const cycles = [];
  const rows =
    (Array.isArray(risk?.processes) && risk.processes.length > 0
      ? risk.processes
      : null) ||
    risk?.process_mappings ||
    risk?.processMappings ||
    [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const cycle = String(row?.cycle_name ?? row?.cycleName ?? row?.cycle_id ?? row?.cycleId ?? '').trim();
    if (!cycle) continue;
    const key = cycle.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cycles.push(cycle);
  }
  if (cycles.length === 0) {
    const legacy = strRiskField(risk, RISK_FIELD_CYCLE_NAME);
    return legacy || '';
  }
  return cycles.join(', ');
}

/** Linked controls as "(controlId) title", comma-separated (deduped). */
function formatRiskExportControlCell(risk) {
  const seen = new Set();
  const parts = [];
  for (const group of riskControlAssociationGroups(risk)) {
    for (const control of Array.isArray(group.controls) ? group.controls : []) {
      const displayId = String(control.controlDisplayId ?? '').trim();
      const title = String(control.controlTitle ?? '').trim();
      const dedupeKey = String(control.controlId || displayId || title)
        .trim()
        .toLowerCase();
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const label = formatRiskExportBracketLabel(displayId, title);
      if (label) parts.push(label);
    }
  }
  return parts.join(', ');
}

/** Scoped to this page so outside-click and layout measurement never hit another popover. */
const RISKS_ASSOCIATION_POPOVER_ATTR = 'data-risks-association-popover';

const PROCESS_FILTER_SELECT_STYLES = {
  placeholder: (base, state) => ({
    ...base,
    fontWeight: 600,
    /** Match the project's placeholder convention: gray (Tailwind gray-400 = #9ca3af) while focused, bold black otherwise. */
    color: state.isFocused ? '#9ca3af' : 'black',
  }),
  singleValue: (base) => ({
    ...base,
    fontWeight: 600,
    color: '#000000',
  }),
  control: (base) => ({
    ...base,
    minHeight: '2.25rem',
    height: '2.25rem',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '2.25rem',
    paddingTop: 0,
    paddingBottom: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '2.25rem',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#eff6ff' : base.backgroundColor,
    color: '#1f2937',
  }),
};

const ProcessFilterOption = (props) => (
  <components.Option {...props}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate">{props.label}</span>
      {props.isSelected ? <Check className="h-4 w-4 text-blue-600 shrink-0" /> : null}
    </div>
  </components.Option>
);

const TABLE_TEXT_LIMIT = 50;
const TABLE_TEXT_BEHAVIOR = 'truncate'; // set to 'wrap' to show full text in next lines
const RISK_CYCLE_CHART_BAR_MAX_PX = 48;
const RISK_CYCLE_CHART_EXTRA_HEIGHT_PX = 32; // count above + cycle label below

function formatTableText(text, behavior = TABLE_TEXT_BEHAVIOR, limit = TABLE_TEXT_LIMIT) {
  const value = String(text ?? '');
  if (behavior === 'wrap' || value.length <= limit) {
    return { value, truncated: false };
  }
  return { value: `${value.slice(0, limit)}...`, truncated: true };
}

function Risks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { primaryColor } = useTheme();
  const { selectedAuditYearId } = useAuditYear();
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(RISKS_DEFAULT_COLUMN_VISIBILITY);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterControlsMapped, setFilterControlsMapped] = useState(CONTROL_MAPPED_FILTER_UNSELECTED);
  const [filterProcessMapped, setFilterProcessMapped] = useState(PROCESS_MAPPED_FILTER_UNSELECTED);
  const [filterProcess, setFilterProcess] = useState('all');
  const [filterCycle, setFilterCycle] = useState('all');
  const [cycleFilterOptions, setCycleFilterOptions] = useState([
    { value: 'all', label: 'All Cycles' },
  ]);
  const [filterSource, setFilterSource] = useState('all');
  const [sortState, setSortState] = useState({ key: null, dir: 'asc' });
  const sortKey = sortState.key;
  const sortDir = sortState.dir;
  /** 1-based page, same pattern as `TestRunsPage`. */
  const [risksListPage, setRisksListPage] = useState(1);
  const [risksPageSize, setRisksPageSize] = useState(readStoredRisksPageSize);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [processFilterSearchInput, setProcessFilterSearchInput] = useState('');
  const [processFilterSearch, setProcessFilterSearch] = useState('');
  const queryClient = useQueryClient();
  useEffect(() => {
    const nextSearch = processFilterSearchInput.trim();
    if (nextSearch === '') {
      setProcessFilterSearch('');
      return;
    }
    const t = window.setTimeout(() => setProcessFilterSearch(nextSearch), 400);
    return () => window.clearTimeout(t);
  }, [processFilterSearchInput]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cycles = await fetchSoxCycles();
        if (cancelled) return;
        setCycleFilterOptions([
          { value: 'all', label: 'All Cycles' },
          ...cycles
            .map((c) => {
              const value = String(c?.id ?? '').trim();
              if (!value) return null;
              const cycleCode = String(c?.cycle_id ?? '').trim();
              const name = String(c?.name ?? '').trim();
              const label = [cycleCode, name].filter(Boolean).join(' - ') || value;
              return { value, label, cycleId: cycleCode };
            })
            .filter(Boolean),
        ]);
      } catch {
        if (!cancelled) {
          setCycleFilterOptions([{ value: 'all', label: 'All Cycles' }]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const processFilterCycleCode = useMemo(() => {
    if (filterCycle === 'all') return '';
    return String(cycleFilterOptions.find((o) => o.value === filterCycle)?.cycleId ?? '').trim();
  }, [filterCycle, cycleFilterOptions]);

  const {
    data: processFilterPages,
    isFetching: loadingProcessFilterOptions,
    isFetchingNextPage: loadingMoreProcessFilterOptions,
    hasNextPage: hasMoreProcessFilterOptions,
    fetchNextPage: fetchMoreProcessFilterOptions,
  } = useRiskProcessFilterOptions(processFilterSearch, '', true, processFilterCycleCode);
  const processFilterOptions = useMemo(() => {
    const items = (processFilterPages?.pages ?? []).flatMap((p) => p.items);
    return [{ value: 'all', label: 'All Processes' }, ...items];
  }, [processFilterPages]);
  const { data: dashboardMetrics } = useRisksDashboardMetrics();
  const processFilterNoOptionsMessage = useMemo(
    () => () => (loadingProcessFilterOptions ? 'Loading processes...' : 'No processes found'),
    [loadingProcessFilterOptions]
  );

  const getRiskKey = compositeRiskKey;

  const [deletingRisks, setDeletingRisks] = useState(false);
  const [associationPopover, setAssociationPopover] = useState(null);
  const associationPopoverRef = useRef(null);
  const [bulkAssociation, setBulkAssociation] = useState(null);
  const [savingBulkAssociation, setSavingBulkAssociation] = useState(false);
  const [bulkProcessSearchInput, setBulkProcessSearchInput] = useState('');
  const [bulkProcessSearch, setBulkProcessSearch] = useState('');
  const [bulkSelectedProcessIds, setBulkSelectedProcessIds] = useState([]);

  useEffect(() => {
    const nextSearch = bulkProcessSearchInput.trim();
    if (nextSearch === '') {
      setBulkProcessSearch('');
      return;
    }
    const t = window.setTimeout(() => setBulkProcessSearch(nextSearch), 400);
    return () => window.clearTimeout(t);
  }, [bulkProcessSearchInput]);

  const {
    data: bulkProcessPages,
    isFetching: loadingBulkProcesses,
    isFetchingNextPage: loadingMoreBulkProcesses,
    hasNextPage: hasMoreBulkProcesses,
    fetchNextPage: fetchMoreBulkProcesses,
  } = useRiskProcessFilterOptions(bulkProcessSearch);

  const bulkProcessOptions = useMemo(
    () => (bulkProcessPages?.pages ?? []).flatMap((p) => p.items),
    [bulkProcessPages]
  );

  /** Same debounce interval as `TestRunsPage` — drives page reset on search. */
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setRisksListPage(1);
  }, [debouncedSearchQuery, filterLevel, filterCategory, filterControlsMapped, filterProcessMapped, filterProcess, filterCycle, filterSource, sortKey, sortDir, selectedAuditYearId]);

  const listParams = useMemo(
    () => ({
      page: risksListPage,
      pageSize: risksPageSize,
      search: debouncedSearchQuery,
      filterLevel,
      filterCategory,
      filterControlsMapped,
      filterProcessMapped,
      filterProcess,
      filterCycle,
      filterSource,
      sortKey,
      sortDir,
      auditYearId: selectedAuditYearId ?? null,
    }),
    [
      risksListPage,
      risksPageSize,
      debouncedSearchQuery,
      filterLevel,
      filterCategory,
      filterControlsMapped,
      filterProcessMapped,
      filterProcess,
      filterCycle,
      filterSource,
      sortKey,
      sortDir,
      selectedAuditYearId,
    ]
  );

  const {
    data: risksListView,
    isLoading: risksListLoading,
    isError: risksListIsError,
    error: risksListError,
  } = useRisksTableList(listParams);

  const pagedTableRows = useMemo(() => risksListView?.rows ?? [], [risksListView]);
  const totalForPagedList = risksListView?.total ?? 0;
  const risksTotalPages = risksListView?.totalPages ?? 1;

  const hasActiveListFilters = useMemo(
    () =>
      debouncedSearchQuery !== '' ||
      filterLevel !== 'all' ||
      filterCategory !== 'all' ||
      filterControlsMapped !== CONTROL_MAPPED_FILTER_UNSELECTED ||
      filterProcessMapped !== PROCESS_MAPPED_FILTER_UNSELECTED ||
      filterProcess !== 'all' ||
      filterCycle !== 'all' ||
      filterSource !== 'all',
    [debouncedSearchQuery, filterLevel, filterCategory, filterControlsMapped, filterProcessMapped, filterProcess, filterCycle, filterSource]
  );

  // Same as Controls: spinner only on first load. keepPreviousData keeps rows
  // visible while filter/sort/page refetch (no table blink).
  const tableLoading = risksListLoading;

  useEffect(() => {
    setRisksListPage((p) => (p > risksTotalPages ? risksTotalPages : p));
  }, [risksTotalPages]);

  const tablePagination = useMemo(() => {
    const page = Math.min(Math.max(1, risksListPage), risksTotalPages);
    return { pageIndex: page - 1, pageSize: risksPageSize };
  }, [risksListPage, risksTotalPages, risksPageSize]);

  const handleTablePaginationChange = useCallback(
    (updater) => {
      const maxP = risksTotalPages;
      const safePrev = Math.min(Math.max(1, risksListPage), maxP);
      const cur = { pageIndex: safePrev - 1, pageSize: risksPageSize };
      const next = typeof updater === 'function' ? updater(cur) : updater;
      if (next.pageSize !== risksPageSize) {
        setRisksPageSize(next.pageSize);
        writeStoredRisksPageSize(next.pageSize);
        setRisksListPage(1);
        return;
      }
      setRisksListPage(Math.min(Math.max(1, next.pageIndex + 1), maxP));
    },
    [risksListPage, risksPageSize, risksTotalPages]
  );

  // Delete single risk
  const handleDeleteRisk = useCallback(async (risk) => {
    if (!window.confirm('Are you sure you want to delete this risk? This will also remove it from the flowchart.')) {
      return;
    }

    setDeletingRisks(true);
    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.delete(
        `/api/process-risks/${risk.id}`,
        { headers: { 'Authorization': userHash || '' } }
      );

      await queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      setSelectedRisks((prev) => prev.filter((k) => k !== getRiskKey(risk)));
    } catch (error) {
      console.error('Error deleting risk:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete risk. Please try again.'));
    } finally {
      setDeletingRisks(false);
    }
  }, [queryClient, getRiskKey]);

  // Delete selected risks (bulk)
  const handleDeleteSelected = async () => {
    if (selectedRisks.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedRisks.length} risk(s)? This will also remove them from the flowcharts.`)) {
      return;
    }

    setDeletingRisks(true);
    try {
      const userHash = localStorage.getItem('userHash');

      const selectedKeySet = new Set(selectedRisks);
      const risksToDelete = pagedTableRows
        .filter((r) => selectedKeySet.has(getRiskKey(r)))
        .map((r) => ({
          riskId: r.id
        }));

      if (risksToDelete.length === 0) {
        alert(
          'No selected risks are on the current page. Open the page where those rows appear, or clear your selection.'
        );
        return;
      }

      if (risksToDelete.length !== selectedRisks.length) {
        const proceed = window.confirm(
          `${selectedRisks.length} risk(s) are selected, but only ${risksToDelete.length} are on this page. Delete only those on this page?`
        );
        if (!proceed) return;
      }

      await axiosInstance.post(
        '/api/process-risks/bulk-delete',
        { risks: risksToDelete },
        { headers: { 'Authorization': userHash || '' } }
      );

      await queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });

      setSelectedRisks([]);
    } catch (error) {
      console.error('Error deleting risks:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete risks. Please try again.'));
    } finally {
      setDeletingRisks(false);
    }
  };


  // Risk Level Badge component
  const RiskLevelBadge = ({ value }) => {
    const getStyles = () => {
      switch (value) {
        case 'High':
          return 'bg-red-100 text-red-700';
        case 'Medium':
          return 'bg-yellow-100 text-yellow-700';
        case 'Low':
          return 'bg-green-100 text-green-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStyles()}`}>
        {value}
      </span>
    );
  };

  // Risk Category Badge component
  const CategoryBadge = ({ value }) => {
    const label =
      value == null || String(value).trim() === '' ? 'SOX' : getRiskCategoryLabel(value);
    const knownCategory = getKnownRiskCategory(label) ?? 'SOX';
    const cls = RISK_CATEGORY_BADGE_CLASSES[knownCategory] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${cls}`}>
        {label}
      </span>
    );
  };

  const handleSortClick = useCallback((key) => {
    setSortState((prev) =>
      prev.key === key
        ? { key: prev.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  }, []);

  const handleUnmappedTileClick = () => {
    setFilterControlsMapped((prev) =>
      prev === CONTROL_MAPPED_FILTER_NO ? CONTROL_MAPPED_FILTER_UNSELECTED : CONTROL_MAPPED_FILTER_NO
    );
  };

  const handleProcessUnmappedTileClick = () => {
    setFilterProcessMapped((prev) =>
      prev === PROCESS_MAPPED_FILTER_NO ? PROCESS_MAPPED_FILTER_UNSELECTED : PROCESS_MAPPED_FILTER_NO
    );
  };

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterLevel('all');
    setFilterCategory('all');
    setFilterControlsMapped(CONTROL_MAPPED_FILTER_UNSELECTED);
    setFilterProcessMapped(PROCESS_MAPPED_FILTER_UNSELECTED);
    setFilterProcess('all');
    setFilterCycle('all');
    setFilterSource('all');
    setSortState({ key: null, dir: 'asc' });
    setRisksListPage(1);
  }, []);

  const handleTotalTileClick = useCallback(() => {
    handleResetFilters();
  }, [handleResetFilters]);
  const handleProcessFilterChange = useCallback((value) => {
    setFilterProcess(typeof value === 'string' ? value : 'all');
  }, []);
  const handleCycleFilterChange = useCallback((e) => {
    setFilterCycle(e.target.value);
    setFilterProcess('all');
  }, []);
  const handleProcessFilterInputChange = useCallback((value, meta) => {
    if (meta.action === 'input-change') setProcessFilterSearchInput(value);
  }, []);
  const handleProcessFilterMenuClose = useCallback(() => {
    setProcessFilterSearchInput('');
  }, []);
  const handleProcessFilterMenuScrollToBottom = useCallback(() => {
    if (hasMoreProcessFilterOptions && !loadingMoreProcessFilterOptions) {
      void fetchMoreProcessFilterOptions();
    }
  }, [fetchMoreProcessFilterOptions, hasMoreProcessFilterOptions, loadingMoreProcessFilterOptions]);

  const renderSortIcon = useCallback(
    (key) => {
      if (sortKey !== key) {
        return <ArrowUpDown className="h-3 w-3 text-gray-400 shrink-0" aria-hidden />;
      }
      return sortDir === 'asc'
        ? <ArrowUp className="h-3 w-3 text-blue-600 shrink-0" aria-hidden />
        : <ArrowDown className="h-3 w-3 text-blue-600 shrink-0" aria-hidden />;
    },
    [sortKey, sortDir]
  );

  const handleAssociationItemAction = useCallback(
    (item) => {
      const kind = associationPopover?.kind;
      if (kind === RISK_ASSOCIATION_KIND.CONTROL) {
        navigate(`/sox-controls/view/${item.controlId}`);
      } else if (kind === RISK_ASSOCIATION_KIND.PROCESS && item.cycleId && item.processId) {
        navigate(soxProcessFlowchartPath(item.cycleId, item.processId), {
          state: buildProcessReturnState(currentPathWithSearch(location)),
        });
      }
      setAssociationPopover(null);
    },
    [associationPopover?.kind, location, navigate]
  );

  const handleExport = () => {
    if (selectedRisks.length === 0) {
      return;
    }

    // Filter the selected risks
    const selectedKeySet = new Set(selectedRisks);
    const selectedData = pagedTableRows.filter((risk) => selectedKeySet.has(getRiskKey(risk)));

    if (selectedData.length === 0) {
      alert(
        'No selected risks are on the current page. Open the page where those rows appear, or clear your selection.'
      );
      return;
    }

    if (selectedData.length !== selectedRisks.length) {
      const proceed = window.confirm(
        `${selectedRisks.length} risk(s) are selected, but only ${selectedData.length} are on this page. Export only those on this page?`
      );
      if (!proceed) return;
    }

    const colVisible = (columnId) => isRiskExportColumnVisible(columnVisibility, columnId);

    // Columns follow gear visibility (defaults + any user-enabled columns).
    // Process Step removed. SOX Cycle filled from linked processes when Process is exported.
    const excelData = selectedData.map((risk) => {
      const row = {};
      if (colVisible('riskId')) {
        row['Risk ID'] = risk.riskId ?? risk.risk_id ?? risk.id ?? '';
      }
      if (colVisible('clientRiskId')) {
        row['Reference ID'] = risk.clientRiskId ?? risk.client_risk_id ?? '';
      }
      if (colVisible('riskDescription')) {
        row['Risk Description'] = strRiskField(risk, RISK_FIELD_RISK_DESCRIPTION);
      }
      if (colVisible('category')) {
        row['Category'] =
          risk.riskCategory == null || String(risk.riskCategory).trim() === ''
            ? 'SOX'
            : getRiskCategoryLabel(risk.riskCategory);
      }
      if (colVisible('classification')) {
        const raw = String(risk.classification ?? 'business').trim().toLowerCase();
        row['Classification'] = raw === 'itgc' ? 'ITGC' : 'Business';
      }
      if (colVisible('riskLevel')) {
        row['Level'] = riskLevelOf(risk) || 'Medium';
      }
      if (colVisible('processes')) {
        row['Process'] = formatRiskExportProcessCell(risk);
        row['SOX Cycle'] = formatRiskExportSoxCycleCell(risk);
      }
      if (colVisible('control')) {
        row['Control'] = formatRiskExportControlCell(risk);
      }
      if (colVisible('originVersion')) {
        const versionNumber = Number(risk.sourceRefVersionNumber ?? risk.source_ref_version_number);
        row['Origin Version'] =
          Number.isFinite(versionNumber) && versionNumber > 0 ? `v${versionNumber}` : '';
      }
      if (colVisible('domain')) {
        const isItgc = String(risk.classification ?? '').trim().toLowerCase() === 'itgc';
        row['Domain'] = isItgc
          ? String(risk.itgcDomainCode ?? risk.itgc_domain_code ?? '').trim()
          : '';
      }
      row['Recommendation'] = strRiskField(risk, RISK_FIELD_RECOMMENDATION);
      return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const colWidthByHeader = {
      'Risk ID': 12,
      'Reference ID': 14,
      'Risk Description': 40,
      Category: 12,
      Classification: 12,
      Level: 10,
      Process: 40,
      'SOX Cycle': 15,
      Control: 40,
      'Origin Version': 14,
      Domain: 14,
      Recommendation: 40,
    };
    const headers = excelData.length > 0 ? Object.keys(excelData[0]) : [];
    worksheet['!cols'] = headers.map((header) => ({ wch: colWidthByHeader[header] || 20 }));

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SOX Risks');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `SOX_Risks_Export_${timestamp}.xlsx`;

    // Export to file
    XLSX.writeFile(workbook, filename);
  };

  // Show all supported categories even if the current dataset does not contain every one of them.
  const uniqueCategories = useMemo(
    () => [...RISK_CATEGORIES, RISK_FILTER_OTHERS],
    []
  );
  const categoryFilterOptions = useMemo(
    () => buildRiskCategoryFilterOptions(uniqueCategories),
    [uniqueCategories]
  );

  const dashboardTotal = dashboardMetrics?.total ?? 0;
  const dashboardUnmapped = dashboardMetrics?.unmapped ?? 0;
  const dashboardProcessUnmapped = dashboardMetrics?.processUnmapped ?? 0;
  const dashboardCycles = dashboardMetrics?.risksByCycle ?? [];
  const cycleBarMax = useMemo(
    () => dashboardCycles.reduce((max, row) => Math.max(max, row.riskCount), 0),
    [dashboardCycles]
  );

  /** Set of selected risk keys for O(1) membership checks in the table cells. */
  const selectedKeySet = useMemo(() => new Set(selectedRisks), [selectedRisks]);

  /** Risk keys for the rows visible on the current page (used by the select-all header). */
  const pageRowKeys = useMemo(() => pagedTableRows.map((r) => getRiskKey(r)), [pagedTableRows, getRiskKey]);

  const allOnPageSelected = useMemo(
    () => pageRowKeys.length > 0 && pageRowKeys.every((k) => selectedKeySet.has(k)),
    [pageRowKeys, selectedKeySet]
  );

  const toggleAllOnPage = useCallback(
    (checked) => {
      if (checked) {
        setSelectedRisks((prev) => {
          const next = new Set(prev);
          pageRowKeys.forEach((k) => next.add(k));
          return Array.from(next);
        });
      } else {
        const pageSet = new Set(pageRowKeys);
        setSelectedRisks((prev) => prev.filter((k) => !pageSet.has(k)));
      }
    },
    [pageRowKeys]
  );

  const toggleRiskKey = useCallback((key, checked) => {
    setSelectedRisks((prev) =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((x) => x !== key)
    );
  }, []);

  const openBulkProcessMapping = useCallback((risk) => {
    const clickedRiskKey = getRiskKey(risk);
    setSelectedRisks((prev) => {
      if (prev.includes(clickedRiskKey)) return prev;
      return [clickedRiskKey];
    });
    setBulkAssociation({ clickedRiskKey });
    setBulkSelectedProcessIds([]);
    setBulkProcessSearchInput('');
    setBulkProcessSearch('');
  }, [getRiskKey]);

  const closeBulkAssociation = useCallback(() => {
    setBulkAssociation(null);
    setBulkSelectedProcessIds([]);
    setBulkProcessSearchInput('');
    setBulkProcessSearch('');
  }, []);

  const selectedRiskIdsForBulk = useMemo(() => {
    const selectedKeySetLocal = new Set(selectedRisks);
    return pagedTableRows
      .filter((risk) => selectedKeySetLocal.has(getRiskKey(risk)))
      .map((risk) => String(risk.id ?? '').trim())
      .filter(Boolean);
  }, [getRiskKey, pagedTableRows, selectedRisks]);

  const handleSaveBulkAssociation = useCallback(async () => {
    if (!bulkAssociation) return;
    if (selectedRiskIdsForBulk.length === 0) {
      alert('Select at least one risk from the table before saving.');
      return;
    }

    if (bulkSelectedProcessIds.length === 0) {
      alert('Select at least one process before saving.');
      return;
    }

    setSavingBulkAssociation(true);
    try {
      await appendRiskProcessMappings({
        riskIds: selectedRiskIdsForBulk,
        processIds: bulkSelectedProcessIds,
        auditYearId: selectedAuditYearId || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      closeBulkAssociation();
    } catch (error) {
      console.error('Failed to append risk associations:', error);
      alert(resolve403ErrorMessage(error, 'Failed to map processes. Please try again.'));
    } finally {
      setSavingBulkAssociation(false);
    }
  }, [bulkAssociation, bulkSelectedProcessIds, closeBulkAssociation, queryClient, selectedRiskIdsForBulk]);

  const associationPopoverUi =
    associationPopover && RISK_ASSOCIATION_UI[associationPopover.kind];

  const getAssociationPopoverPosition = useCallback((rect, kind, measuredHeight) => {
    /** Match popover max widths: controls 28rem (448px), process w-80 (320px). */
    const popoverWidth =
      kind === RISK_ASSOCIATION_KIND.CONTROL
        ? Math.min(448, window.innerWidth - 16)
        : Math.min(320, window.innerWidth - 16);
    /**
     * Prefer real rendered height so flip/clamp logic stays anchored to the clicked row.
     * Fallback estimates are used only before the popover is mounted/measured.
     */
    const fallbackHeight = kind === RISK_ASSOCIATION_KIND.CONTROL ? 420 : 320;
    const estimatedHeight =
      typeof measuredHeight === 'number' && Number.isFinite(measuredHeight) && measuredHeight > 0
        ? measuredHeight
        : fallbackHeight;
    const hGutter = 4;
    const vGutter = 8;
    const viewportPadding = 8;
    const minLeft = hGutter;
    const maxLeft = Math.max(minLeft, window.innerWidth - popoverWidth - hGutter);

    let left;
    if (kind === RISK_ASSOCIATION_KIND.PROCESS) {
      /** Process popover: open right of anchor; if clipped, fall back left. */
      let preferred = rect.right + hGutter;
      if (preferred > maxLeft) {
        preferred = rect.left - popoverWidth - hGutter;
        if (preferred < minLeft) preferred = rect.right - popoverWidth;
      }
      left = Math.min(Math.max(preferred, minLeft), maxLeft);
    } else {
      /** Controls popover: open left of anchor; if clipped, align to anchor right edge. */
      let preferred = rect.left - popoverWidth - hGutter;
      if (preferred < minLeft) preferred = rect.right - popoverWidth;
      left = Math.min(Math.max(preferred, minLeft), maxLeft);
    }

    const preferredBelow = rect.bottom + vGutter;
    const preferredAbove = rect.top - estimatedHeight - vGutter;
    const fitsBelow = preferredBelow + estimatedHeight <= window.innerHeight - viewportPadding;
    const fitsAbove = preferredAbove >= viewportPadding;
    const top = fitsBelow
      ? preferredBelow
      : fitsAbove
        ? preferredAbove
        : Math.max(
            viewportPadding,
            Math.min(preferredBelow, window.innerHeight - viewportPadding - estimatedHeight)
          );

    return { top, left };
  }, []);

  const openAssociationPopover = useCallback((event, risk, kind) => {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const anchor = trigger.closest('[data-association-anchor]') ?? trigger;
    const rect = anchor.getBoundingClientRect();
    const { top, left } = getAssociationPopoverPosition(rect, kind);

    if (kind === RISK_ASSOCIATION_KIND.CONTROL) {
      const controlGroups = riskControlAssociationGroups(risk);
      if (controlGroups.length === 0) return;
      setAssociationPopover({
        kind,
        riskId: risk.id,
        top,
        left,
        items: [],
        controlGroups,
        anchorEl: anchor,
      });
      return;
    }

    const items = riskAssociationEntries(risk, kind);
    if (items.length === 0) return;
    setAssociationPopover({
      kind,
      riskId: risk.id,
      top,
      left,
      items,
      controlGroups: null,
      anchorEl: anchor,
    });
  }, [getAssociationPopoverPosition]);

  /**
   * Selection column is split out of `riskTableColumns` so changing page or selection state
   * doesn't force the rest of the columns (and their cell renderers) to rebuild.
   */
  const selectColumn = useMemo(
    () => ({
      id: 'select',
      columnLabel: 'Select',
      widthPercent: 5,
      header: () => (
        <input
          type="checkbox"
          className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          onChange={(e) => toggleAllOnPage(e.target.checked)}
          checked={allOnPageSelected}
          aria-label="Select all risks on this page"
        />
      ),
      cell: ({ row }) => (
        <div
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            checked={selectedKeySet.has(getRiskKey(row.original))}
            onChange={(e) => toggleRiskKey(getRiskKey(row.original), e.target.checked)}
            aria-label="Select risk"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false
    }),
    [allOnPageSelected, toggleAllOnPage, selectedKeySet, toggleRiskKey, getRiskKey]
  );

  const riskTableColumns = useMemo(
    () => [
      selectColumn,
      {
        id: 'riskId',
        columnLabel: 'Risk ID',
        widthPercent: 10,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(RISK_SORT_KEY.RISK_ID)}
            className="flex items-center gap-1 hover:text-gray-800 font-medium uppercase tracking-wider text-xs rounded px-0.5 -mx-0.5"
          >
            Risk ID
            {renderSortIcon(RISK_SORT_KEY.RISK_ID)}
          </button>
        ),
        cell: ({ row }) => {
          const sysId = row.original.riskId ?? row.original.risk_id ?? row.original.id;
          return sysId != null && sysId !== '' ? (
            <span className="text-sm text-gray-500">{String(sysId)}</span>
          ) : (
            <p className="text-sm text-gray-400 text-center">-</p>
          );
        },
        enableSorting: false
      },
      {
        id: 'clientRiskId',
        columnLabel: 'Reference ID',
        widthPercent: 12,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(RISK_SORT_KEY.CLIENT_RISK_ID)}
            className="flex items-center gap-1 hover:text-gray-800 font-medium uppercase tracking-wider text-xs rounded px-0.5 -mx-0.5"
          >
            Reference ID
            {renderSortIcon(RISK_SORT_KEY.CLIENT_RISK_ID)}
          </button>
        ),
        cell: ({ row }) => {
          const value = row.original.clientRiskId ?? row.original.client_risk_id ?? '';
          return value ? (
            <span className="text-sm text-gray-700 font-medium">{String(value)}</span>
          ) : (
            <p className="text-sm text-gray-400 text-center">-</p>
          );
        },
        enableSorting: false
      },
      {
        id: 'riskDescription',
        columnLabel: 'Risk Description',
        widthPercent: 24,
        header: () => (
          <button
            type="button"
            onClick={() => handleSortClick(RISK_SORT_KEY.RISK_DESCRIPTION)}
            className="flex items-center gap-1 hover:text-gray-800 font-medium uppercase tracking-wider text-xs text-left rounded px-0.5 -mx-0.5"
          >
            Risk Description
            {renderSortIcon(RISK_SORT_KEY.RISK_DESCRIPTION)}
          </button>
        ),
        cell: ({ row }) => {
          const risk = row.original;
          const description = strRiskField(risk, RISK_FIELD_RISK_DESCRIPTION);
          const step = strRiskField(risk, RISK_FIELD_NODE_DESCRIPTION);
          if (!description && !step) {
            return <p className="text-sm text-gray-400 text-center">-</p>;
          }
          const formattedDescription = formatTableText(description || '-');
          const descriptionClassName =
            TABLE_TEXT_BEHAVIOR === 'wrap'
              ? 'text-sm text-gray-900 whitespace-normal break-words'
              : 'text-sm text-gray-900';
          const formattedStep = formatTableText(step || '');
          const stepClassName =
            TABLE_TEXT_BEHAVIOR === 'wrap'
              ? 'text-xs text-gray-500 mt-1 whitespace-normal break-words'
              : 'text-xs text-gray-500 mt-1';
          return (
            <div>
              <p
                className={descriptionClassName}
                title={formattedDescription.truncated ? description : undefined}
              >
                {formattedDescription.value}
              </p>
              {step ? (
                <p
                  className={stepClassName}
                  title={formattedStep.truncated ? step : undefined}
                >
                  Step: {formattedStep.value}
                </p>
              ) : null}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'category',
        columnLabel: 'Category',
        widthPercent: 10,
        header: () => (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => handleSortClick(RISK_SORT_KEY.CATEGORY)}
              className="flex items-center justify-center gap-1 hover:text-gray-800 font-medium uppercase tracking-wider text-xs rounded px-0.5"
            >
              Category
              {renderSortIcon(RISK_SORT_KEY.CATEGORY)}
            </button>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <CategoryBadge value={row.original.riskCategory} />
          </div>
        ),
        enableSorting: false
      },
      {
        id: 'classification',
        columnLabel: 'Classification',
        widthPercent: 8,
        header: () => (
          <div className="flex justify-center">
            <span className="font-medium uppercase tracking-wider text-xs">Classification</span>
          </div>
        ),
        cell: ({ row }) => {
          const raw = String(row.original.classification ?? 'business').trim().toLowerCase();
          const label = raw === 'itgc' ? 'ITGC' : 'Business';
          const chipClass =
            raw === 'itgc' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
          return (
            <div className="flex justify-center">
              <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${chipClass}`}>
                {label}
              </span>
            </div>
          );
        },
        enableSorting: false
      },
      {
        id: 'riskLevel',
        columnLabel: 'Level',
        widthPercent: 8,
        header: () => (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => handleSortClick(RISK_SORT_KEY.RISK_LEVEL)}
              className="flex items-center justify-center gap-1 hover:text-gray-800 font-medium uppercase tracking-wider text-xs rounded px-0.5"
            >
              Level
              {renderSortIcon(RISK_SORT_KEY.RISK_LEVEL)}
            </button>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <RiskLevelBadge value={riskLevelOf(row.original) || 'Medium'} />
          </div>
        ),
        enableSorting: false
      },
      ...RISK_ASSOCIATION_COLUMN_ORDER.map((kind) => {
        const ui = RISK_ASSOCIATION_UI[kind];
        return {
          id: ui.columnId,
          columnLabel: ui.columnLabel,
          widthPercent: 8,
          header: () => (
            <div className="flex justify-center">
              <span className="font-medium uppercase tracking-wider text-xs">{ui.headerLabel}</span>
            </div>
          ),
          cell: ({ row }) => {
            const isItgc =
              String(row.original.classification ?? '').trim().toLowerCase() === 'itgc';
            const canAddProcess = kind === RISK_ASSOCIATION_KIND.PROCESS && !isItgc;
            return (
              <div className="flex justify-center whitespace-nowrap">
                <AssociationCountCell
                  count={riskAssociationCount(row.original, kind)}
                  triggerDataAttr="data-association-trigger"
                  onClick={(e) => openAssociationPopover(e, row.original, kind)}
                  onAdd={canAddProcess ? () => openBulkProcessMapping(row.original) : undefined}
                  addLabel={canAddProcess ? 'Add processes' : undefined}
                />
              </div>
            );
          },
          enableSorting: false,
        };
      }),
      {
        id: 'originVersion',
        columnLabel: 'Origin Version',
        widthPercent: 10,
        hideable: true,
        header: () => (
          <div className="flex justify-center">
            <span className="font-medium uppercase tracking-wider text-xs">Origin Version</span>
          </div>
        ),
        cell: ({ row }) => {
          const versionNumber = Number(
            row.original.sourceRefVersionNumber ?? row.original.source_ref_version_number
          );
          if (!Number.isFinite(versionNumber) || versionNumber <= 0) {
            return <p className="text-sm text-gray-400 text-center">-</p>;
          }
          return (
            <div className="flex justify-center">
              <span className="inline-flex items-center rounded px-2 py-1 text-xs font-medium bg-green-100 text-green-700">
                {`v${versionNumber}`}
              </span>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'domain',
        columnLabel: 'Domain',
        widthPercent: 8,
        hideable: true,
        header: () => (
          <div className="flex justify-center">
            <span className="font-medium uppercase tracking-wider text-xs">Domain</span>
          </div>
        ),
        cell: ({ row }) => {
          const isItgc =
            String(row.original.classification ?? '').trim().toLowerCase() === 'itgc';
          if (!isItgc) {
            return <p className="text-sm text-gray-400 text-center">-</p>;
          }
          const code = String(
            row.original.itgcDomainCode ??
              row.original.itgc_domain_code ??
              row.original.itgcDomain?.code ??
              row.original.itgc_domain?.code ??
              ''
          ).trim();
          return code ? (
            <span className="text-sm text-gray-700">{code}</span>
          ) : (
            <p className="text-sm text-gray-400 text-center">-</p>
          );
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        columnLabel: 'Actions',
        widthPercent: 7,
        header: () => (
          <div className="flex justify-center">
            <span className="font-medium uppercase tracking-wider text-xs">Actions</span>
          </div>
        ),
        cell: ({ row }) => {
          const risk = row.original;
          return (
            <div
              className="flex items-center justify-center gap-1"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  const id = risk.riskId ?? risk.risk_id ?? risk.id;
                  navigate(TENANT_PATHS.SOX_RISKS_VIEW.replace(':riskId', encodeURIComponent(String(id))));
                }}
                className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                title="View risk"
                aria-label="View risk"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleDeleteRisk(risk)}
                disabled={deletingRisks}
                className="inline-flex items-center justify-center rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50"
                title="Delete risk"
                aria-label="Delete risk"
              >
                {deletingRisks ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </div>
          );
        },
        enableSorting: false
      },
    ],
    [
      selectColumn,
      deletingRisks,
      handleDeleteRisk,
      handleSortClick,
      navigate,
      openBulkProcessMapping,
      openAssociationPopover,
      renderSortIcon,
    ]
  );

  useEffect(() => {
    const handleDocumentInteraction = (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      if (
        associationPopover &&
        !t.closest(`[${RISKS_ASSOCIATION_POPOVER_ATTR}]`) &&
        !t.closest('[data-association-trigger]')
      ) {
        setAssociationPopover(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentInteraction);
    return () => {
      document.removeEventListener('mousedown', handleDocumentInteraction);
    };
  }, [associationPopover]);

  useEffect(() => {
    if (!associationPopover?.anchorEl) return undefined;

    let rafId = null;
    const updatePopoverPosition = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setAssociationPopover((prev) => {
          if (!prev?.anchorEl) return prev;
          if (!document.body.contains(prev.anchorEl)) return null;
          const rect = prev.anchorEl.getBoundingClientRect();
          const measuredHeight = associationPopoverRef.current?.getBoundingClientRect?.().height;
          const next = getAssociationPopoverPosition(rect, prev.kind, measuredHeight);
          if (next.top === prev.top && next.left === prev.left) return prev;
          return { ...prev, ...next };
        });
      });
    };

    const scrollOptions = { passive: true, capture: true };
    window.addEventListener('scroll', updatePopoverPosition, scrollOptions);
    window.addEventListener('resize', updatePopoverPosition, { passive: true });
    updatePopoverPosition();
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePopoverPosition, scrollOptions);
      window.removeEventListener('resize', updatePopoverPosition);
    };
  }, [associationPopover?.anchorEl, getAssociationPopoverPosition]);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Risks</h1>
            <p className="text-sm text-gray-600 mt-1">View and manage identified SOX risks across all processes</p>
          </div>
          <div className="flex items-center gap-2">
            <ModuleAuditDownloadButton
              resourceType="risk"
              moduleKey={MODULE_KEYS.RISKS}
              filenamePrefix="risk_audit"
            />
            <button
              type="button"
              onClick={() => navigate(TENANT_PATHS.SOX_RISKS_CREATE)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              style={primaryColor ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
            >
              <Plus className="h-4 w-4" />
              New Risk
            </button>
          </div>
        </div>

        {/* Compact dashboard — totals, unmapped counts, top cycles (single row on md+) */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <button
            type="button"
            onClick={handleTotalTileClick}
            className="text-left rounded-lg shadow-sm p-4 transition-all hover:shadow-md border border-gray-200 bg-white ring-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 min-w-0"
            aria-label="Show all risks"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 leading-snug min-h-[2.5rem]">Risks identified</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xl font-bold leading-none tabular-nums text-[#4f46e5]">{dashboardTotal}</p>
                <div className="stat-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e0e7ff] text-[#4f46e5]" aria-hidden="true">
                  <ClipboardList className="h-5 w-5" strokeWidth={2} />
                </div>
              </div>
            </div>
          </button>
          <button
            type="button"
            aria-pressed={filterControlsMapped === CONTROL_MAPPED_FILTER_NO}
            onClick={handleUnmappedTileClick}
            className={`text-left rounded-lg shadow-sm p-4 transition-all hover:shadow-md ring-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 min-w-0 ${filterControlsMapped === CONTROL_MAPPED_FILTER_NO
                ? 'border-2 border-[#b45309] bg-[#fff7ed]'
                : 'border border-gray-200 bg-white'
              }`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 leading-snug min-h-[2.5rem]">Not mapped to control</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xl font-bold leading-none tabular-nums text-[#c2410c]">{dashboardUnmapped}</p>
                <div className="stat-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffedd5] text-[#c2410c]" aria-hidden="true">
                  <ShieldOff className="h-5 w-5" strokeWidth={2} />
                </div>
              </div>
            </div>
          </button>
          <button
            type="button"
            aria-pressed={filterProcessMapped === PROCESS_MAPPED_FILTER_NO}
            onClick={handleProcessUnmappedTileClick}
            className={`text-left rounded-lg shadow-sm p-4 transition-all hover:shadow-md ring-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 min-w-0 ${filterProcessMapped === PROCESS_MAPPED_FILTER_NO
                ? 'border-2 border-[#0369a1] bg-[#f0f9ff]'
                : 'border border-gray-200 bg-white'
              }`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 leading-snug min-h-[2.5rem]">Not mapped to process</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xl font-bold leading-none tabular-nums text-[#0369a1]">{dashboardProcessUnmapped}</p>
                <div className="stat-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e0f2fe] text-[#0369a1]" aria-hidden="true">
                  <Link2Off className="h-5 w-5" strokeWidth={2} />
                </div>
              </div>
            </div>
          </button>
          <div className="md:col-span-3 rounded-lg shadow-sm p-4 border border-gray-200 bg-white min-w-0">
            <p className="text-sm font-medium text-gray-500 mb-3">Risks by cycle (top 7)</p>
            {dashboardCycles.length === 0 ? (
              <p className="text-sm text-gray-400">No cycle mappings yet</p>
            ) : (
              <div
                className="flex items-end gap-2"
                style={{ height: RISK_CYCLE_CHART_BAR_MAX_PX + RISK_CYCLE_CHART_EXTRA_HEIGHT_PX }}
                role="img"
                aria-label={`Risks by cycle: ${dashboardCycles.map((row) => `${row.cycleId || row.cycleName} ${row.riskCount}`).join(', ')}`}
              >
                {dashboardCycles.map((row, index) => {
                  const barHeightPx =
                    cycleBarMax > 0
                      ? Math.max(4, Math.round((row.riskCount / cycleBarMax) * RISK_CYCLE_CHART_BAR_MAX_PX))
                      : 4;
                  const label = row.cycleId || row.cycleName;
                  const barTone = ['bg-blue-400', 'bg-blue-500', 'bg-indigo-400', 'bg-slate-400', 'bg-teal-400', 'bg-violet-400', 'bg-cyan-400'][index] ?? 'bg-blue-400';
                  return (
                    <div key={`${label}-${index}`} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-0.5 h-full">
                      <span className="text-[10px] font-bold text-gray-700 leading-none tabular-nums">
                        {row.riskCount}
                      </span>
                      <div
                        className={`w-full max-w-[2rem] rounded-t shrink-0 ${barTone}`}
                        style={{ height: barHeightPx }}
                        title={`${label}: ${row.riskCount}`}
                      />
                      <span className="text-[10px] font-semibold text-gray-500 truncate max-w-full leading-none" title={label}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>



        {/* Search and Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-[12rem]">
              <ListPageSearchInput
                placeholder="Search risks by ID, Reference or Description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputClassName="bg-gray-50"
              />
            </div>

            <AppSelect
              wrapperClassName="w-36 shrink-0"
              size="control"
              value={filterControlsMapped}
              placeholder="Controls Mapped(All)"
              onChange={(e) => setFilterControlsMapped(e.target.value)}
              aria-label="Filter by controls mapped"
              options={[
                { label: 'Controls Mapped(All)', value: CONTROL_MAPPED_FILTER_UNSELECTED },
                ...CONTROL_MAPPED_FILTER_OPTIONS,
              ]}
            />

            <AppSelect
              wrapperClassName="w-28 shrink-0"
              size="control"
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              aria-label="Filter by risk level"
              options={RISK_LEVEL_FILTER_OPTIONS}
            />

            <AppSelect
              wrapperClassName="w-28 shrink-0"
              size="control"
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              aria-label="Filter by source"
              options={SOURCE_FILTER_OPTIONS}
            />

            <AppSelect
              wrapperClassName="w-32 shrink-0"
              size="control"
              value={filterCycle}
              onChange={handleCycleFilterChange}
              aria-label="Filter by cycle"
              options={cycleFilterOptions}
            />

            <div className="w-32 shrink-0">
              <Select
                value={filterProcess}
                onChange={handleProcessFilterChange}
                isSearchable
                aria-label="Filter by process"
                options={processFilterOptions}
                onInputChange={handleProcessFilterInputChange}
                onMenuClose={handleProcessFilterMenuClose}
                onMenuScrollToBottom={handleProcessFilterMenuScrollToBottom}
                components={{
                  Option: ProcessFilterOption,
                }}
                loadingMoreOptions={loadingMoreProcessFilterOptions}
                styles={PROCESS_FILTER_SELECT_STYLES}
                noOptionsMessage={processFilterNoOptionsMessage}
                placeholder="All Processes"
                placeholderOnFocus="Type something to search"
                placeholderOnBlur="All Processes"
                className="w-full"
              />
            </div>

            <AppSelect
              wrapperClassName="w-32 shrink-0"
              size="control"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              aria-label="Filter by category"
              options={categoryFilterOptions}
            />

            <Tooltip content="Reset filters" side="left">
              <button
                type="button"
                onClick={handleResetFilters}
                aria-label="Reset filters"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>

        <AppDataTable
          className="[&_.app-table-scroll-wrap]:overflow-x-auto [&_table]:w-full [&_th]:px-2 [&_td]:px-2 [&_th]:py-2 [&_td]:py-2 [&_th]:whitespace-normal [&_th]:break-words [&_th]:align-top [&_td]:whitespace-nowrap [&_td]:overflow-hidden"
          mode="server"
          data={pagedTableRows}
          columns={riskTableColumns}
          loading={tableLoading}
          error={risksListIsError ? risksListError?.message ?? 'Failed to load risks' : null}
          paginated
          pagination={tablePagination}
          onPaginationChange={handleTablePaginationChange}
          rowCount={totalForPagedList}
          pageCount={risksTotalPages}
          paginationItemLabel="risks"
          showPageSizeSelector
          showColumnVisibilityMenu
          enableColumnReorder
          defaultColumnVisibility={RISKS_DEFAULT_COLUMN_VISIBILITY}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          allowColumnOverflowScroll
          headerStart={
            selectedRisks.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-blue-900">
                  {selectedRisks.length} {selectedRisks.length === 1 ? 'item' : 'items'} selected of {totalForPagedList}{' '}
                  {totalForPagedList === 1 ? 'risk' : 'risks'}
                </span>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deletingRisks}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deletingRisks ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
                <Tooltip content="Cancel selections" side="top">
                  <button
                    onClick={() => setSelectedRisks([])}
                    className="p-1 text-blue-700 hover:text-blue-900 transition-colors"
                    aria-label="Cancel selections"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <h2 className="text-lg font-medium text-gray-900">
                Identified Risks ({totalForPagedList})
              </h2>
            )
          }
          getRowId={(row) => getRiskKey(row)}
          isRowSelected={(row) => selectedKeySet.has(getRiskKey(row))}
          emptyMessage={
            totalForPagedList === 0
              ? hasActiveListFilters
                ? 'No risks found. Try clearing filters or search to see more risks.'
                : 'No risks found. Risks will appear here once you identify them in your process flowcharts.'
              : 'No data available'
          }
        />

        <AssociationPopover
          ref={associationPopoverRef}
          isOpen={!!associationPopover}
          dataAttr={RISKS_ASSOCIATION_POPOVER_ATTR}
          top={associationPopover?.top}
          left={associationPopover?.left}
          title={associationPopoverUi?.popoverTitle ?? ''}
          items={associationPopover?.items || []}
          primaryTextKey={associationPopoverUi?.primaryTextKey ?? 'processName'}
          secondaryTextKey={associationPopoverUi?.secondaryTextKey ?? null}
          actionLabel={associationPopoverUi?.actionLabel ?? ''}
          controlGroups={associationPopover?.controlGroups ?? null}
          onItemAction={handleAssociationItemAction}
        />

        <BulkAssociationModal
          isOpen={!!bulkAssociation}
          selectedRiskCount={selectedRiskIdsForBulk.length}
          saving={savingBulkAssociation}
          processSelectionCount={bulkSelectedProcessIds.length}
          onClose={closeBulkAssociation}
          onSave={handleSaveBulkAssociation}
        >
          <RiskProcessAssociations
            selectedValues={bulkSelectedProcessIds}
            processOptions={bulkProcessOptions}
            fixedTagContainer
            loadingOptions={loadingBulkProcesses || loadingMoreBulkProcesses}
            loadingMoreOptions={loadingMoreBulkProcesses}
            searchInputValue={bulkProcessSearchInput}
            hasMoreOptions={Boolean(hasMoreBulkProcesses)}
            onSearchChange={setBulkProcessSearchInput}
            onLoadMore={() => {
              if (hasMoreBulkProcesses && !loadingMoreBulkProcesses) {
                void fetchMoreBulkProcesses();
              }
            }}
            onAdd={(value) => {
              setBulkSelectedProcessIds((prev) => (prev.includes(value) ? prev : [...prev, value]));
            }}
            onRemove={(value) => {
              setBulkSelectedProcessIds((prev) => prev.filter((v) => v !== value));
            }}
          />
        </BulkAssociationModal>

      </div>
      </div>
    </TooltipProvider>
  );
}

export default Risks;
